import { FormattedMessage, useIntl } from 'react-intl';
import { Helmet } from 'react-helmet';
import { useHistory } from 'react-router';
import { Form, Divider, message } from 'antd';

import CustomerGeneralInfoForm from '@/components/Customers/CustomerGeneralInfoForm';
import TabContentWrapper from '@/components/_common/TabContentWrapper';
import { SaveButton } from '@/components/_common/Buttons';
import { PageProps } from '@/types/PageProps';
import {
  useCreateCustomer,
  useUpdateCustomerShowLGPD,
} from '@/core/domain/people/Customers/Customers.hooks';
import { CreateCustomerRequest } from '@/core/domain/people/Customers/Customers.types';
import Authorities from '@/types/Authority';
import SpaceForButton from '@/components/_common/SpaceForButtons';
import ButtonBack from '@/components/_common/ButtonBack';
import { useDisclosure } from '@/hooks';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import { CustomerContact } from '@/core/domain/people/CustomerContacts/CustomerContacts.types';
import { useState } from 'react';
import { isAppPortocar } from '@/index';

const CreateCustomerPage = ({ route }: PageProps) => {
  const intl = useIntl();
  const [reminder, setReminder] = useDisclosure();

  const history = useHistory();
  const [form] = Form.useForm();
  const mutation = useCreateCustomer();
  const remindShowLgpd = useUpdateCustomerShowLGPD();

  const [contacts, setContacts] = useState<CustomerContact[]>([]);

  const handleSaveClick = async () => {
    try {
      const { recommendation, address, ...values } =
        await form.validateFields();

      if (isAppPortocar && !contacts.length) {
        message.destroy();
        message.error('Informe pelo menos um contato de e-mail e telefone!');
        form.setFields([
          { name: 'portoContacts', errors: ['Preencha um e-mail e telefone!'] },
        ]);
        return;
      }

      const obj: CreateCustomerRequest = recommendation?.id
        ? {
            ...values,
            recommendation,
            cpfCnpj: values.cpfCnpj || undefined,
            foreignDocument: values.foreignDocument || undefined,
            active: true,
            rentalCompany: !!values.rentalCompany,
          }
        : { ...values, cpfCnpj: values.cpfCnpj || undefined, active: true };

      const result = await mutation.mutateAsync({
        ...obj,
        addresses: address
          ? [{ ...address, delivery: !!address.delivery, main: true }]
          : undefined,
        contacts: contacts.map((contact) => ({
          ...contact,
          id: undefined,
          detailList: contact.detailList.map((detail) => ({
            ...detail,
            id: undefined,
          })),
        })),
      });

      if (reminder) {
        await remindShowLgpd
          .mutateAsync(result.id)
          .then(() => history.push(`/app/customers/${result.id}`));
      } else {
        history.push(`/app/customers/${result.id}`);
      }
    } catch {}
  };

  return (
    <TabContentWrapper
      permissions={route.permissions}
      title={route.name}
      extra={
        <SaveButton
          block
          onClick={handleSaveClick}
          loading={mutation.isLoading}
          disabled={!hasPermissions([Authorities.REGISTER_CUSTOMER_ADD])}
        >
          <FormattedMessage id="commons.entity.save" />
        </SaveButton>
      }
    >
      <Helmet title={intl.formatMessage({ id: route.name })} />

      <CustomerGeneralInfoForm
        form={form}
        remind={reminder}
        setReminderShow={setReminder.toggle}
        contacts={contacts}
        setContacts={setContacts}
      />

      <Divider />

      <SpaceForButton>
        <ButtonBack path="/app/customers/list" />
      </SpaceForButton>
    </TabContentWrapper>
  );
};

export default CreateCustomerPage;
